import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from "react"
import { fetchAppLookups, checkAuth, login, logout } from "../services/auth"
import { toast } from "react-toastify"

const AuthContext = createContext(null)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const storedUser = localStorage.getItem("user")
    return storedUser ? JSON.parse(storedUser) : null
  })
  const [loading, setLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [appLookups, setAppLookups] = useState(null)

  useEffect(() => {
    const initAuth = async () => {
      try {
        // Check authentication
        const isAuthenticated = await checkAuth()
        setIsAuthenticated(isAuthenticated)

        // If authenticated, try to load lookups and restore user
        if (isAuthenticated) {
          // First check if user data exists in localStorage
          const storedUser = localStorage.getItem("user")
          if (!storedUser) {
            // If no user data found, log out
            console.warn("No user data found in localStorage, logging out")
            await doLogout()
            return
          }

          // Then check localStorage for lookups
          const storedAppLookups = localStorage.getItem("appLookups")
          if (storedAppLookups) {
            setAppLookups(JSON.parse(storedAppLookups))
          } else {
            // If no stored lookups, fetch from server
            const fetchedAppLookups = await fetchAppLookups()
            if (fetchedAppLookups) {
              setAppLookups(fetchedAppLookups)
              localStorage.setItem("appLookups", JSON.stringify(fetchedAppLookups))
            }
          }
        } else {
          // If not authenticated, clear stored data
          localStorage.removeItem("user")
          localStorage.removeItem("appLookups")
          setUser(null)
          setAppLookups(null)
        }
      } catch (error) {
        console.error("Auth check failed:", error)
        setIsAuthenticated(false)
        localStorage.removeItem("user")
        localStorage.removeItem("appLookups")
        setUser(null)
        setAppLookups(null)
      } finally {
        setLoading(false)
      }
    }

    initAuth().then()
  }, [])

  const doLogin = useCallback(async (username, password) => {
    try {
      const userData = await login(username, password)

      if (userData) {
        // Store user data
        localStorage.setItem("user", JSON.stringify(userData))
        setUser(userData)
        setIsAuthenticated(true)

        // Fetch lookups after successful login
        const fetchedAppLookups = await fetchAppLookups()
        if (fetchedAppLookups) {
          setAppLookups(fetchedAppLookups)
          localStorage.setItem("appLookups", JSON.stringify(fetchedAppLookups))
        }
      }
      return userData
    } catch (error) {
      console.log(error)
      setIsAuthenticated(false)
      localStorage.removeItem("user")
      setUser(null)
    }
  }, [])

  const doLogout = useCallback(async () => {
    try {
      await logout()
      setUser(null)
      setIsAuthenticated(false)
      setAppLookups(null)
      // Clear all storage data
      localStorage.clear()
      sessionStorage.clear()
      // Dismiss all toasts when logging out
      toast.dismiss()
    } catch (error) {
      console.error("Logout failed:", error)
      throw error
    }
  }, [])

  // Lookup helper method
  const getAppLookupValue = useCallback((table, code, subCode = null) => {
    if (code === undefined || code === null || code === "") return ""

    if (!appLookups || !appLookups.some(v => v.Table === table)) {
      toast.warning(`Δεν βρέθηκαν τιμές αναφοράς για τον πίνακα: ${table}`, { autoClose: 3000 })
      return code
    }

    // Special handling for SubStatus table
    if (table === "SubStatus" && subCode !== null) {
      const lookup = appLookups
        .filter(v => v.Table === table)
        .find(
          item => item.Code.toString() === code.toString() && item.SubCode.toString() === subCode.toString()
        )

      if (!lookup) {
        toast.warning(
          `Δεν βρέθηκε τιμή αναφοράς για τον πίνακα: ${table}, κωδικός: ${code}, υποκωδικός: ${subCode}`,
          { autoClose: 3000 }
        )
      }
      return lookup?.Name ?? code
    }

    // Normal lookup for other tables
    const lookup = appLookups
      .filter(v => v.Table === table)
      .find(item => item.Code.toString() === code.toString())

    if (!lookup) {
      //TODO: Uncomment this when we have a way to handle this
      /*toast.warning(`Δεν βρέθηκε τιμή αναφοράς για τον πίνακα: ${table}, κωδικός: ${code}`, {
        autoClose: 3000
      })*/
    }
    return lookup?.Name ?? code
  }, [appLookups])

  // Admin check helper method
  const isAdmin = useCallback(() => {
    return user?.RoleID === 0
  }, [user])

  const isGarage = useCallback(() => {
    return user?.RoleID === 2
  }, [user])

  const isExpert = useCallback(() => {
    return user?.RoleID === 1
  }, [user])

  const authContextValue = useMemo(
    () => ({
      user,
      doLogin,
      doLogout,
      isAuthenticated,
      loading,
      appLookups,
      getAppLookupValue,
      isAdmin,
      isGarage,
      isExpert
    }),
    [
      user,
      doLogin,
      doLogout,
      isAuthenticated,
      loading,
      appLookups,
      getAppLookupValue,
      isAdmin,
      isGarage,
      isExpert
    ]
  )

  if (loading) {
    return (
      <div className="min-vh-100 d-flex justify-content-center align-items-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    )
  }

  return (
    <AuthContext.Provider value={authContextValue}>
      {children}
    </AuthContext.Provider>
  )
}
